import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Lock, Mail, ArrowRight } from "lucide-react";
import { toast, Toaster } from "sonner";
import { authApi } from "./api";
import { BRAND } from "../brand";
import { usePageMeta } from "../hooks/usePageMeta";

export default function AdminLogin() {
  usePageMeta({ title: "Admin · Daos Ads" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.login(email.trim().toLowerCase(), password);
      toast.success("Bienvenido David");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "No se pudo iniciar sesión";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-radial-orange opacity-70 pointer-events-none" />
      <div
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1100px] h-[900px] rounded-full opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, rgba(255,138,0,0.18), transparent 70%)" }}
      />
      <div className="absolute inset-0 grain pointer-events-none" />

      <div className="relative min-h-screen flex flex-col">
        <header className="container-x py-6">
          <Link to="/" className="inline-flex items-center gap-2.5" data-testid="admin-login-logo">
            <img src={BRAND.logo} alt="Daos Ads" className="w-9 h-9 rounded-full ring-1 ring-white/10" />
            <div className="leading-tight">
              <div className="font-display font-bold text-white text-[15px]">Daos Ads</div>
              <div className="text-[10px] text-zinc-500 tracking-[0.18em] uppercase">Panel Admin</div>
            </div>
          </Link>
        </header>

        <div className="flex-1 grid place-items-center px-6 pb-16">
          <div className="w-full max-w-md reveal is-visible">
            <div className="grad-border p-8 sm:p-10 relative overflow-hidden">
              <div className="absolute -top-32 -right-20 w-[420px] h-[420px] bg-gradient-to-br from-[#ff3b1f]/20 to-transparent rounded-full blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="eyebrow mb-5">Acceso restringido</div>
                <h1 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
                  Inicia sesión en el <span className="text-gradient">CRM Daos</span>.
                </h1>
                <p className="mt-3 text-zinc-400 text-sm">Solo para David Osorio. El acceso se registra y se limita por intentos.</p>

                <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
                  <Field
                    icon={Mail}
                    label="Correo"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="david.osorio@daosads.com"
                    name="email"
                  />
                  <Field
                    icon={Lock}
                    label="Contraseña"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    name="password"
                  />

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="admin-login-submit"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {loading ? "Entrando..." : "Entrar al panel"}
                    {!loading && <ArrowRight className="w-4 h-4" />}
                  </button>
                </form>
              </div>
            </div>

            <p className="text-zinc-500 text-xs text-center mt-5">
              ¿No eres David? <Link to="/" className="text-zinc-300 hover:text-white">Volver al sitio</Link>
            </p>
          </div>
        </div>
      </div>

      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: { background: "#0a0a0a", border: "1px solid rgba(255,138,0,0.25)", color: "#fff" },
        }}
      />
    </div>
  );
}

function Field({ icon: Icon, label, name, ...rest }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-zinc-400 font-display font-semibold mb-2">
        <Icon className="w-3.5 h-3.5 text-[#ff8a00]" />
        {label}
      </label>
      <input
        {...rest}
        name={name}
        data-testid={`admin-login-${name}`}
        autoComplete={name === "password" ? "current-password" : "email"}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none"
      />
    </div>
  );
}
